import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useApp } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiClipboard, FiBarChart3, FiTarget, FiUsers, FiTrendingUp, FiCheckCircle, FiAward, FiAlertTriangle, FiHome, FiArchive, FiUser, FiLogOut, FiSettings } = FiIcons;

const Sidebar = ({ isOpen, onClose, user, onLogout }) => {
  const location = useLocation();
  const { state } = useApp();
  
  // Determine if the course is active (not in draft status)
  const isActive = state.activeCourse?.status === 'active';
  
  // Define all menu items
  const allMenuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiHome, alwaysShow: true },
    { path: '/set-plo', label: 'Set PLO', icon: FiSettings, hideWhenActive: true },
    { path: '/', label: 'Assessment Plan', icon: FiClipboard, hideWhenActive: true },
    { path: '/slt-weightage', label: 'SLT & Weightage', icon: FiBarChart3, hideWhenActive: true },
    { path: '/clo-plo-mapping', label: 'CLO-PLO Mapping', icon: FiTarget, hideWhenActive: true },
    { path: '/marks-entry', label: 'Marks Entry', icon: FiUsers, alwaysShow: true },
    { path: '/course-performance', label: 'Course Performance', icon: FiTrendingUp, alwaysShow: true },
    { path: '/clo-analysis', label: 'CLO Analysis', icon: FiCheckCircle, alwaysShow: true },
    { path: '/plo-analysis', label: 'PLO Analysis', icon: FiAward, alwaysShow: true },
    { path: '/failure-report', label: 'Failure Report', icon: FiAlertTriangle, alwaysShow: true },
    { path: '/archived-reports', label: 'Archived Reports', icon: FiArchive, alwaysShow: true },
    { path: '/profile', label: 'Profile', icon: FiUser, alwaysShow: true }
  ];

  // Filter menu items based on active course status
  const filteredMenuItems = allMenuItems.filter(item => {
    if (item.alwaysShow) return true;
    if (isActive && item.hideWhenActive) return false;
    return true;
  });

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={{ x: -300 }}
        animate={{ x: isOpen ? 0 : -300 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed left-0 top-0 h-full w-64 bg-white shadow-lg z-50 lg:translate-x-0 lg:static lg:z-auto"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:hidden">
          <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Desktop header - only show on large screens when sidebar is always visible */}
        <div className="hidden lg:flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
          {state.activeCourse && (
            <div className="text-sm text-gray-500 font-medium">
              {state.activeCourse.course_code || state.activeCourse.courseCode}
            </div>
          )}
        </div>

        <nav className="mt-6">
          {filteredMenuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`flex items-center space-x-3 px-4 py-3 mx-2 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <SafeIcon icon={item.icon} className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <button
            onClick={onLogout}
            className="flex items-center space-x-3 w-full px-4 py-3 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiLogOut} className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;